/**
 * Job ranking engine with query parsing, filtering, and advanced scoring
 * Integrates BM25 search with business logic for job search relevance
 */

import { parseQueryToFilters } from '../search.js';
import { BM25Index } from './bm25.js';
import { tokenize } from './tokenizer.js';

/**
 * Job search ranking engine
 */
export class JobRanker {
  constructor() {
    this.index = new BM25Index();
    this.isInitialized = false;
  }

  /**
   * Initializes the ranking engine with job data
   * @param {Array} jobData - Array of job documents
   * @param {Object} options - Initialization options
   */
  initialize(jobData = [], options = {}) {
    const { fieldWeights = {}, enableDebugMode = false } = options;
    
    // Set custom field weights if provided
    if (Object.keys(fieldWeights)?.length > 0) {
      this.index?.setFieldWeights(fieldWeights);
    }

    this.debugMode = enableDebugMode;
    this.isInitialized = false;

    // Clear existing index
    this.index?.clear();

    // Add all jobs to index
    this._indexJobs(jobData);

    this.isInitialized = true;

    if (this.debugMode) {
      console.log('JobRanker initialized:', this.index?.getStats());
    }
  }

  /**
   * Adds jobs to the BM25 index
   * @param {Array} jobs - Job documents to index
   */
  _indexJobs(jobs) {
    jobs?.forEach(job => {
      // Skip invalid jobs
      if (!job?.id || !job?.title) return;

      // Prepare metadata for enhanced scoring
      const metadata = {
        postedAt: job?.postedAt,
        validThrough: job?.validThrough,
        status: job?.status || 'ACTIVE',
        isFeatured: job?.isFeatured || false,
        workMode: job?.workMode,
        employmentType: job?.employmentType,
        experienceLevel: job?.experienceLevel,
        salary: job?.salary,
        matchScore: job?.matchScore,
        locations: job?.locations
      };

      this.index?.addDocument(job?.id, job, metadata);
    });
  }

  /**
   * Ranks jobs based on query and filters with advanced scoring
   * @param {string} query - Search query string
   * @param {Object} options - Ranking options and filters
   * @returns {Object} - Ranked results with metadata
   */
  rankJobs(query = '', options = {}) {
    if (!this.isInitialized) {
      throw new Error('JobRanker not initialized. Call initialize() first.');
    }

    const {
      limit = 25,
      offset = 0,
      userLocation = null,
      filters = {},
      enableGeoBoost = true,
      enableFreshnessDecay = true,
      includeDebugInfo = false
    } = options;

    const startTime = performance?.now();

    // Parse query into structured filters if needed
    const parsedFilters = query ? parseQueryToFilters(query) : {};
    
    // Merge with explicit filters (explicit filters take precedence)
    const mergedFilters = { ...parsedFilters, ...filters };

    // Extract pure search terms (remove filter keywords)
    const searchQuery = this._extractSearchTerms(query);

    // Prepare search options for BM25 index
    const searchOptions = {
      limit: Math.min(limit * 2, 100), // Get more results for re-ranking
      offset: 0, // Always start from 0 for re-ranking
      userLocation: enableGeoBoost ? userLocation : null,
      filters: mergedFilters,
      queryContext: {
        originalQuery: query,
        searchTerms: searchQuery,
        experienceLevel: mergedFilters?.experienceLevel,
        workMode: mergedFilters?.workMode,
        employmentType: mergedFilters?.employmentType
      },
      includeInactive: mergedFilters?.includeInactive || false
    };

    // Get initial results from BM25
    const bm25Results = this.index?.search(searchQuery, searchOptions);

    // Apply post-processing and re-ranking
    const rerankedResults = this._postProcessResults(bm25Results, {
      query,
      searchQuery,
      userLocation,
      enableGeoBoost,
      enableFreshnessDecay,
      includeDebugInfo
    });

    // Apply final pagination
    const finalResults = rerankedResults?.slice(offset, offset + limit);

    const endTime = performance?.now();
    const searchTime = endTime - startTime;

    // Prepare response
    const response = {
      results: finalResults,
      totalResults: rerankedResults?.length,
      searchTime: Math.round(searchTime * 100) / 100, // Round to 2 decimal places
      query: {
        original: query,
        searchTerms: searchQuery,
        filters: mergedFilters
      },
      pagination: {
        limit,
        offset,
        hasMore: offset + limit < rerankedResults?.length
      }
    };

    if (includeDebugInfo) {
      response.debug = {
        indexStats: this.index?.getStats(),
        bm25ResultCount: bm25Results?.length,
        rerankedResultCount: rerankedResults?.length,
        averageScore: finalResults?.length > 0 
          ? finalResults?.reduce((sum, r) => sum + (r?._score || 0), 0) / finalResults?.length
          : 0
      };
    }

    return response;
  }

  /**
   * Extracts search terms from query, removing filter keywords
   * @param {string} query - Original query string
   * @returns {string} - Clean search terms
   */
  _extractSearchTerms(query) {
    if (!query?.trim()) return '';

    // Remove location indicators and common filter words
    let cleanQuery = query
      ?.replace(/\b(in|at|near|location:|loc:)\s+[^,\s]+(?:,\s*[^,\s]+)*/gi, '')
      ?.replace(/\b(remote|hybrid|onsite|on-site)\b/gi, '')
      ?.replace(/\b(full-time|part-time|contract|temporary|intern|internship)\b/gi, '')
      ?.replace(/\b(today|24h|week|month|7d|30d)\b/gi, '')
      ?.replace(/\bsalary:\s*[\d,]+\s*-\s*[\d,]+/gi, '')
      ?.replace(/\s+/g, ' ')
      ?.trim();

    return cleanQuery;
  }

  /**
   * Post-processes BM25 results with additional business logic
   * @param {Array} results - Results from BM25 search
   * @param {Object} context - Processing context
   * @returns {Array} - Re-ranked results
   */
  _postProcessResults(results, context) {
    const {
      query,
      searchQuery,
      userLocation,
      enableGeoBoost,
      enableFreshnessDecay,
      includeDebugInfo
    } = context;

    return results?.map(result => {
      let finalScore = result?._score || 0;
      const scoringFactors = { base: finalScore };

      // Apply title exact match boost
      if (searchQuery && result?.title) {
        const titleBoost = this._calculateTitleMatchBoost(searchQuery, result?.title);
        finalScore *= titleBoost;
        scoringFactors.titleBoost = titleBoost;
      }

      // Apply company popularity boost
      const companyBoost = this._calculateCompanyPopularityBoost(result);
      finalScore *= companyBoost;
      scoringFactors.companyBoost = companyBoost;

      // Apply skill match boost
      if (searchQuery && result?.tags) {
        const skillBoost = this._calculateSkillMatchBoost(searchQuery, result?.tags);
        finalScore *= skillBoost;
        scoringFactors.skillBoost = skillBoost;
      }

      // Apply application competition penalty
      const competitionPenalty = this._calculateCompetitionPenalty(result);
      finalScore *= competitionPenalty;
      scoringFactors.competitionPenalty = competitionPenalty;

      // Apply diversity boost (promote variety in results)
      const diversityBoost = this._calculateDiversityBoost(result, results);
      finalScore *= diversityBoost;
      scoringFactors.diversityBoost = diversityBoost;

      const processedResult = {
        ...result,
        _score: finalScore,
        _originalScore: result?._score
      };

      if (includeDebugInfo) {
        processedResult._scoringFactors = scoringFactors;
      }

      return processedResult;
    })?.sort((a, b) => (b?._score || 0) - (a?._score || 0));
  }

  /**
   * Calculates boost for exact or partial title matches
   * @param {string} query - Search query
   * @param {string} title - Job title
   * @returns {number} - Boost factor (1.0 = no boost)
   */
  _calculateTitleMatchBoost(query, title) {
    if (!query || !title) return 1.0;

    const queryLower = query?.toLowerCase();
    const titleLower = title?.toLowerCase();

    // Exact match - highest boost
    if (titleLower === queryLower) return 2.0;

    // Title starts with query - high boost
    if (titleLower?.startsWith(queryLower)) return 1.8;

    // Title contains full query - medium boost
    if (titleLower?.includes(queryLower)) return 1.5;

    // Check for word matches
    const queryWords = tokenize(query, { removeStopWords: true, applyStemming: false });
    const titleWords = tokenize(title, { removeStopWords: true, applyStemming: false });

    if (queryWords?.length === 0) return 1.0;

    const matchCount = queryWords?.filter(word => 
      titleWords?.some(titleWord => titleWord?.includes(word) || word?.includes(titleWord))
    )?.length;

    const matchRatio = matchCount / queryWords?.length;

    // Apply boost based on match ratio
    if (matchRatio >= 0.8) return 1.4;
    if (matchRatio >= 0.6) return 1.3;
    if (matchRatio >= 0.4) return 1.2;
    if (matchRatio >= 0.2) return 1.1;

    return 1.0;
  }

  /**
   * Calculates company popularity boost based on job count and recency
   * @param {Object} result - Job result
   * @returns {number} - Boost factor
   */
  _calculateCompanyPopularityBoost(result) {
    // Featured companies get a boost
    if (result?.isFeatured || result?.companyName === 'Featured Company') {
      return 1.2;
    }

    // Well-known company indicators
    const companyName = result?.companyName?.toLowerCase() || '';
    
    // Tech companies boost
    if (companyName?.includes('google') || companyName?.includes('microsoft') || 
        companyName?.includes('apple') || companyName?.includes('amazon')) {
      return 1.15;
    }

    // Companies with many active jobs get slight boost
    if (result?.openRoles > 10) return 1.1;
    if (result?.openRoles > 5) return 1.05;

    return 1.0;
  }

  /**
   * Calculates skill match boost for technical queries
   * @param {string} query - Search query
   * @param {Array|string} jobTags - Job tags/skills
   * @returns {number} - Boost factor
   */
  _calculateSkillMatchBoost(query, jobTags) {
    if (!query || !jobTags) return 1.0;

    const tags = Array.isArray(jobTags) ? jobTags : [jobTags];
    const queryTerms = tokenize(query, { removeStopWords: false, applyStemming: false });
    
    let exactMatches = 0;
    let partialMatches = 0;

    queryTerms?.forEach(term => {
      const termLower = term?.toLowerCase();
      tags?.forEach(tag => {
        const tagLower = tag?.toLowerCase();
        if (tagLower === termLower) {
          exactMatches++;
        } else if (tagLower?.includes(termLower) || termLower?.includes(tagLower)) {
          partialMatches++;
        }
      });
    });

    // Calculate boost based on matches
    let boost = 1.0;
    if (exactMatches > 0) boost += exactMatches * 0.15;
    if (partialMatches > 0) boost += partialMatches * 0.08;

    return Math.min(boost, 2.0); // Cap at 2.0x boost
  }

  /**
   * Calculates penalty based on application competition
   * @param {Object} result - Job result
   * @returns {number} - Penalty factor (less than 1.0 = penalty)
   */
  _calculateCompetitionPenalty(result) {
    // Check location statuses for application counts
    const locationStatuses = result?.locationStatuses || {};
    let maxApplications = 0;

    Object?.values(locationStatuses)?.forEach(status => {
      if (status?.applications > maxApplications) {
        maxApplications = status?.applications;
      }
    });

    // Apply penalty for high competition jobs
    if (maxApplications > 100) return 0.9;
    if (maxApplications > 50) return 0.95;
    if (maxApplications > 25) return 0.98;

    return 1.0; // No penalty
  }

  /**
   * Calculates diversity boost to promote variety in results
   * @param {Object} result - Current job result
   * @param {Array} allResults - All results for context
   * @returns {number} - Boost factor
   */
  _calculateDiversityBoost(result, allResults) {
    // Count similar jobs in top results (first 10)
    const topResults = allResults?.slice(0, 10);
    let similarCount = 0;

    topResults?.forEach(topResult => {
      // Same company
      if (topResult?.companyName === result?.companyName) similarCount++;
      
      // Same location
      if (topResult?.locations?.[0]?.city === result?.locations?.[0]?.city) {
        similarCount += 0.5;
      }
      
      // Same category
      if (topResult?.category === result?.category) similarCount += 0.3;
    });

    // Apply diversity boost (favor unique jobs)
    if (similarCount === 0) return 1.1;
    if (similarCount <= 1) return 1.05;
    if (similarCount <= 2) return 1.0;
    if (similarCount <= 3) return 0.95;
    
    return 0.9;
  }

  /**
   * Gets suggestions for query improvement or related searches
   * @param {string} query - Original query
   * @param {Array} results - Current results
   * @returns {Array} - Suggestions for better search
   */
  getSuggestions(query, results = []) {
    if (!this.isInitialized) return [];

    const suggestions = [];

    // If no results, suggest broader terms
    if (results?.length === 0) {
      const queryTerms = tokenize(query);
      if (queryTerms?.length > 1) {
        suggestions?.push({
          type: 'broader',
          text: queryTerms?.slice(0, 1)?.join(' '),
          reason: 'Try fewer keywords'
        });
      }
    }

    // If many results, suggest refinement
    if (results?.length > 50) {
      suggestions?.push({
        type: 'refine',
        text: `${query} location:your-city`,
        reason: 'Add location to narrow results'
      });
    }

    // Suggest popular related searches based on common terms
    const relatedSearches = this._getRelatedSearches(query);
    suggestions?.push(...relatedSearches);

    return suggestions?.slice(0, 5); // Limit to 5 suggestions
  }

  /**
   * Gets related search suggestions
   * @param {string} query - Original query  
   * @returns {Array} - Related search suggestions
   */
  _getRelatedSearches(query) {
    const suggestions = [];
    const queryLower = query?.toLowerCase();

    // Programming language related searches
    if (queryLower?.includes('javascript') || queryLower?.includes('js')) {
      suggestions?.push({
        type: 'related',
        text: 'react developer',
        reason: 'Popular with JavaScript'
      });
    }

    if (queryLower?.includes('python')) {
      suggestions?.push({
        type: 'related', 
        text: 'data scientist python',
        reason: 'Popular with Python'
      });
    }

    // Role-based suggestions
    if (queryLower?.includes('engineer')) {
      suggestions?.push({
        type: 'related',
        text: 'senior engineer',
        reason: 'Related role level'
      });
    }

    return suggestions;
  }

  /**
   * Gets detailed analytics about search performance
   * @returns {Object} - Search analytics data
   */
  getAnalytics() {
    if (!this.isInitialized) return null;

    return {
      indexStats: this.index?.getStats(),
      isInitialized: this.isInitialized,
      debugMode: this.debugMode
    };
  }
}

// Create a singleton instance for the application
export const jobRanker = new JobRanker();

export default jobRanker;